import { Box, CircularProgress, Dialog, Grid, IconButton, Typography, useMediaQuery } from '@mui/material';
import { CloseIcon } from '@/app/icons/CloseIcon';
import { ImageViewButton } from '@/components/view/image/ImageViewButton';
import { ImageViewControls } from '@/components/view/image/ImageViewControls';
import { MESSAGE_IMAGE_LOAD_FAILED } from '@/utils/validation-utils';
import { RightIcon } from '@/app/icons/RightIcon';
import { Theme } from '@mui/material/styles';
import React, { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';

interface Props {
  open: boolean;
  title?: ReactNode;
  content: string;
  /**
   * Whether the bytes of the image are still being read. The dialog is already on screen by then,
   * so it shows a spinner in place of the image.
   */
  isLoading?: boolean;
  /**
   * Whether the read of the bytes failed. The dialog says so and stays open, so a walk through a
   * list is not ended by one original that has gone missing.
   */
  isFailed?: boolean;
  /**
   * Shows the image before this one, when the caller opened the dialog on a list. Absent for a
   * single image, which draws no arrows at all.
   */
  onPreviousImage?: () => void;
  /**
   * Shows the image after this one, when the caller opened the dialog on a list.
   */
  onNextImage?: () => void;
  onClose?: () => void;
}

/** How far the image is from being on screen: its bytes and its decoding are two separate waits. */
type ImageStatus = 'loading' | 'loaded' | 'failed';

const zoomStep = 0.25;
const maxScale = 5;
const minScale = 0.5;
const defaultScale = 1;
const defaultRotate = 0;
const quarterTurn = 90;
const fullTurn = 360;
/**
 * How much room the floating controls take at the bottom, in theme units: they sit `spacing(4)`
 * off the bottom edge and stand `spacing(6)` tall, so a fitted image has to stop above them.
 */
const controlsRoom = 11;
/**
 * How much room the paging arrows take at either side, in theme units: they stand `spacing(2)` off
 * the edge and are `spacing(6)` wide, so a fitted image clears them on anything wider than a phone.
 */
const arrowRoom = 9;
/** How long the image is waited for before the spinner gives way to the failure text, in milliseconds. */
const imageWaitTimeout = 15000;

export const ImageViewDialog: FC<Props> = (props: Props) => {
  const { open, title, content, isLoading, isFailed: readFailed, onPreviousImage, onNextImage, onClose } = props;
  const isMobile: boolean = useMediaQuery((theme: Theme) => {
    return theme.breakpoints.down('sm');
  });

  const imageRef = useRef<HTMLImageElement | null>(null);
  const [scale, setScale] = useState<number>(defaultScale);
  const [rotate, setRotate] = useState<number>(defaultRotate);
  const [status, setStatus] = useState<ImageStatus>('loading');

  const handleTurnRight = (): void => {
    setRotate((current: number): number => (current + quarterTurn) % fullTurn);
  };
  const handleTurnLeft = (): void => {
    setRotate((current: number): number => (current + fullTurn - quarterTurn) % fullTurn);
  };
  const handleZoomIn = (): void => {
    setScale((current: number): number => Math.min(current + zoomStep, maxScale));
  };
  const handleZoomOut = (): void => {
    setScale((current: number): number => Math.max(current - zoomStep, minScale));
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent): void => {
      if (e.key === 'ArrowLeft' && onPreviousImage) {
        onPreviousImage();
      } else if (e.key === 'ArrowRight' && onNextImage) {
        onNextImage();
      }
    },
    [onNextImage, onPreviousImage],
  );

  // The listener is global, so a closed dialog must not keep it: the dialog stays mounted next to
  // the page it belongs to, and ←/→ would otherwise page through images from anywhere on screen.
  useEffect(() => {
    if (!open) {
      return;
    }
    window.addEventListener('keydown', handleKeyDown);
    return (): void => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, open]);

  // Another image is another wait: zoom and rotation start over, so a photo opened after one that
  // was turned upright is not itself turned. A source the browser has already decoded reports
  // itself complete, which would leave no load event to wait for.
  useEffect(() => {
    setScale(defaultScale);
    setRotate(defaultRotate);
    const decoded: boolean = !!imageRef.current?.complete && imageRef.current.naturalWidth > 0;
    setStatus(decoded ? 'loaded' : 'loading');
  }, [content, open]);

  // Bytes that never decode — a truncated or corrupt original — must not leave the sender watching
  // a spinner, so the wait is bounded and ends in the failure text.
  useEffect(() => {
    if (!open || status !== 'loading') {
      return;
    }
    const timer: number = window.setTimeout((): void => setStatus('failed'), imageWaitTimeout);
    return (): void => {
      window.clearTimeout(timer);
    };
  }, [content, open, status]);

  const isFailed: boolean = !isLoading && (!!readFailed || (!!content && status === 'failed'));
  const isWaiting: boolean = !isFailed && (!!isLoading || !content || status === 'loading');
  // A rotated image has its width and height swapped on screen, so it has to be measured against
  // the swapped viewer box to keep fitting; unsupported container units simply leave it scrollable.
  const isQuarterTurned: boolean = rotate % (2 * quarterTurn) !== 0;
  const isPageable: boolean = !!onPreviousImage || !!onNextImage;

  return (
    <Dialog
      open={open}
      fullScreen={isMobile}
      PaperProps={{
        sx: (theme: Theme) => ({
          overflow: 'hidden',
          [theme.breakpoints.up('sm')]: {
            margin: theme.spacing(5),
            width: '100%',
            maxWidth: `calc(100% - ${theme.spacing(10)})`,
            height: '100%',
            maxHeight: `calc(100% - ${theme.spacing(10)})`,
          },
        }),
      }}>
      <Grid container={true} direction={'column'} height={'100%'} flexWrap={'nowrap'}>
        <Grid item={true}>
          <Box padding={(theme: Theme) => theme.spacing(2)}>
            <Grid
              container={true}
              direction={'row'}
              justifyContent={'space-between'}
              alignItems={'start'}
              wrap={'nowrap'}
              spacing={2}>
              <Grid item={true}>{title}</Grid>
              <Grid item={true}>
                {onClose && (
                  <IconButton
                    disableRipple={true}
                    aria-label={'image-view-close'}
                    sx={(theme: Theme) => ({
                      display: 'flex',
                      padding: theme.spacing(0.375),
                    })}
                    onClick={onClose}>
                    <CloseIcon
                      sx={{
                        color: (theme: Theme) => theme.colors.textColor,
                        width: (theme: Theme) => theme.spacing(2.25),
                        height: (theme: Theme) => theme.spacing(2.25),
                      }}
                    />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item={true} xs={true} position={'relative'}>
          <Box
            aria-label={'image-view-frame'}
            sx={(theme: Theme) => ({
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              // The controls float over the image, and the arrows stand at its sides, so a fitted
              // image has to stop short of them rather than sit underneath. A phone has no width to
              // spare for side gutters, so there the arrows overlay the image as a gallery's do.
              paddingBottom: theme.spacing(controlsRoom),
              ...(isPageable
                ? {
                    [theme.breakpoints.up('sm')]: {
                      paddingLeft: theme.spacing(arrowRoom),
                      paddingRight: theme.spacing(arrowRoom),
                    },
                  }
                : {}),
              // An image at its own size always fits, so only a zoomed one is worth panning around.
              overflow: scale === defaultScale ? 'hidden' : 'auto',
              containerType: 'size',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            })}>
            {isFailed && (
              <Typography variant={'body2'} aria-label={'image-view-failed'}>
                {MESSAGE_IMAGE_LOAD_FAILED}
              </Typography>
            )}
            {content && !isFailed && (
              <img
                ref={imageRef}
                src={`data:image/jpeg;base64,${content}`}
                alt={''}
                style={{
                  maxWidth: isQuarterTurned ? '100cqh' : '100%',
                  maxHeight: isQuarterTurned ? '100cqw' : '100%',
                  objectFit: 'contain',
                  transform: `rotate(${rotate}deg) scale(${scale})`,
                  transformOrigin: 'center center',
                  transition: 'transform 300ms ease-in-out',
                  // The image is in the tree while it decodes, so that its load and error events arrive.
                  opacity: status === 'loaded' ? 1 : 0,
                }}
                onLoad={(): void => setStatus('loaded')}
                onError={(): void => setStatus('failed')}
              />
            )}
            {isWaiting && (
              <Box
                aria-label={'image-view-progress'}
                sx={{
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <CircularProgress color={'primary'} />
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
      {onPreviousImage && (
        <Box
          sx={{
            position: 'absolute',
            left: (theme: Theme) => theme.spacing(2),
            top: '50%',
            transform: 'translateY(-50%)',
          }}>
          <ImageViewButton aria-label={'image-view-previous'} onClick={onPreviousImage}>
            <RightIcon sx={{ transform: 'scale(-1, 1)' }} />
          </ImageViewButton>
        </Box>
      )}
      {onNextImage && (
        <Box
          sx={{
            position: 'absolute',
            right: (theme: Theme) => theme.spacing(2),
            top: '50%',
            transform: 'translateY(-50%)',
          }}>
          <ImageViewButton aria-label={'image-view-next'} onClick={onNextImage}>
            <RightIcon />
          </ImageViewButton>
        </Box>
      )}
      {!isWaiting && !isFailed && (
        <Box
          sx={{
            position: 'absolute',
            left: (theme: Theme) => theme.spacing(4),
            right: (theme: Theme) => theme.spacing(4),
            bottom: (theme: Theme) => theme.spacing(4),
          }}>
          <ImageViewControls
            canZoomIn={scale < maxScale}
            canZoomOut={scale > minScale}
            onTurnRight={handleTurnRight}
            onTurnLeft={handleTurnLeft}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
          />
        </Box>
      )}
    </Dialog>
  );
};
